/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {Moment} from 'moment';
import Device from '@ion/core/src/services/Device';
import {EventCode} from '@ion/core/src/services/EventCodes';
import {DashboardId} from '@ion/core/src/services/Sisense';
import {
  GenericObject,
  HVSRecord
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {CreateGeoFenceTypeEnum, DateSelectorValues} from '@ion/dls/src/types/types';
import {getAssetHistorySetting} from '../../hooks/useAssetHistorySettings';
import {PageState} from '../../types/pages';
import {ReportsMode} from '../../types/redux';
import {
  APPLY_ASSETS_FILTER,
  APPLY_CATEGORIES_FILTER,
  APPLY_DEVICES_FILTER,
  APPLY_EVENTS_FILTER,
  APPLY_GROUPS_FILTER,
  APPLY_TYPES_FILTER,
  ASSET_FILTER_LOADING,
  CLEAR_FILTERS,
  DATE_FILTER_REQUEST,
  GET_ASSETS_HISTORY_ERROR,
  GET_ASSETS_HISTORY_REQUEST,
  GET_ASSETS_HISTORY_RESPONSE,
  GET_DATE_FILTERS_ERROR,
  GET_DATE_FILTERS_REQUEST,
  GET_DATE_FILTERS_RESPONSE,
  GET_EVENTS_FILTERS_ERROR,
  GET_EVENTS_FILTERS_REQUEST,
  GET_EVENTS_FILTERS_RESPONSE,
  GET_FILTER_REQUEST,
  GET_LOCATIONS_FILTERS_ERROR,
  GET_LOCATIONS_FILTERS_REQUEST,
  GET_LOCATIONS_FILTERS_RESPONSE,
  GET_OPERATORS_FILTERS_ERROR,
  GET_OPERATORS_FILTERS_REQUEST,
  GET_OPERATORS_FILTERS_RESPONSE,
  INITIALIZE_REPORTS,
  REGISTER_TOKEN_SUCCESS,
  REMOVE_ASSETS_HISTORY_REQUEST,
  SET_ASSETS_HISTORY_LOADING,
  SET_ASSETS_HISTORY_REPORT_ERROR,
  SET_BOUNDARY_PANEL_OPEN,
  SET_DATE_FILTERS_REQUEST,
  SET_DVR_ASSETS,
  SET_EVENTS_FILTERS_REQUEST,
  SET_GRID_COLUMNS,
  SET_HISTORY_ASSETS,
  SET_HISTORY_DATE_RANGE,
  SET_HISTORY_GRID_FILTER_MODEL,
  SET_HISTORY_GROUPS,
  SET_HISTORY_MAP_PROPS,
  SET_HOVERED_HISTORY_EVENT,
  SET_IS_MOBILE_MAP_VIEW,
  SET_LOCATIONS_FILTERS_REQUEST,
  SET_OPERATORS_FILTERS_REQUEST,
  SET_SELECTED_BOUNDARY_TOOL,
  SET_SHIPPING_GRID_COLUMNS,
  SET_SISENSE_DEFAULT_DASHBOARD,
  SET_TIMELINE_DATE_RANGE,
  TOGGLE_DISTANCE_TRAVELLED_CARD,
  TOGGLE_DRIVING_TIME_CARD,
  TOGGLE_GRID_ASSETS_EXPANDED,
  TOGGLE_GRID_GROUPS_EXPANDED,
  TOGGLE_HARSH_EVENTS_CARD,
  TOGGLE_IDLING_TIME_CARD,
  TOGGLE_SHOW_EVENTS_BETWEEN_TRIPS,
  TOGGLE_SHOW_SUMMARY_CARD,
  TOGGLE_SHOW_SUMMARY_CARDS,
  TOGGLE_SHOW_TRIP_VISIBLE,
  TOGGLE_TIMESLIDER,
  TOGGLE_TOTAL_ASSETS_CARD,
  UPDATE_DATE_VALUE,
  UPDATE_GRID_COLUMNS,
  UPDATE_SHIPPING_GRID_COLUMNS
} from '../actions/reports';
import {SET_SELECTED_ASSET} from '../actions/tracking';
import {
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns,
  updateTripVisible
} from '../helpers';

export type ReportsHistory = {
  assets: Device[] | null;
  assetsLoading: boolean;
  hoveredEvent: HVSRecord | null;
  history: any[] | null;
  historyByAsset: GenericObject;
  loading: boolean;
  error: ServiceError | null;
  startDate: Moment | null,
  geometryRings: number[];
  endDate: Moment | null;
  dateValue: string;
  filterLoading: boolean;
  expandedGroups: string[];
  expandedAssets: string[];
  isMobileMapView: boolean;
};

export type ShipmentHistory = {
  filters?: ReportsFilter;
}

export interface ReportPreferences {
  showSummaryCards: boolean;
  showTripVisible: boolean;
  showEventsBetweenTrips: boolean;
  showTotalAssets: boolean;
  showDistanceTravelled: boolean;
  showDrivingTime: boolean;
  showIdlingTime: boolean;
  showHarshEvents: boolean;
  isAssetSnapToRoadEnabled: boolean;
  showStatusEvents: boolean;
};

export type ReportSettings = {
  showEventsBetweenTrips: boolean;
  showTripVisible: boolean;
  showSummaryCards: boolean;
  showDistanceTravelled: boolean;
  showIdlingTime: boolean;
  showDrivingTime: boolean;
  showHarshEvents: boolean;
  showTotalAssets: boolean;
};

type FilterOptions = {
  loading: boolean;
  error: ServiceError | null,
  items: any[],
  selected: any[]
};

export type DateOptions = {
  startDate: Moment
  endDate: Moment
  dateValue: string
}
export interface ReportsFilter {
  operators?: FilterOptions,
  events?: FilterOptions,
  locations?: FilterOptions,
  date?: GenericObject,
  groups?: FilterOptions,
  types?: FilterOptions,
  categories?: FilterOptions,
  assetCategories?: FilterOptions,
  assetTypes?: FilterOptions,
  assets?: FilterOptions,
  devices?: FilterOptions,
};

export interface FilterHistoryParams {
  history: HVSRecord[];
  filters: ReportsFilter;
  defaultEvents?: EventCode[]
}

export interface Grid {
  columnState: ColumnState[],
  preferedColumnState: ColumnState[],
  hasUpdate: boolean
};

export interface Timeline {
  start: any,
  end: any,
  isTimeSliderOn: boolean
};

export interface ErrorMessage {
  show: boolean;
  currentRequest: GenericObject;
};

export interface ReportsState extends PageState {
  isInitialized: boolean;
  isTokenRegistered: boolean;
  sisense: {
    defaultDashboard: DashboardId;
  };
  grid: Grid,
  shippingGrid: Grid,
  boundaryPanelOpen: boolean;
  selectedBoundaryTool: CreateGeoFenceTypeEnum;
  history: ReportsHistory;
  filters: ReportsFilter;
  timeline: Timeline;
  errorMsg: ErrorMessage;
  settings: ReportSettings;
  shipmentHistory: ShipmentHistory;
  dvrAssets: GenericObject;
  mapProps: GenericObject;
  gridFilters: {
    filterModel: FilterModel | {}
  }
};

const PREFERENCE          = 'asset-history';
const SHIPPING_PREFERENCE = 'shipping-history';

// defines the initial state for the reducer ...
export const initialState: ReportsState = {
  isInitialized     : false,
  isTokenRegistered : false,
  sisense           : {
    defaultDashboard : null
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  },
  gridFilters : {
    filterModel : {}
  },
  mapProps     : {},
  shippingGrid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  },
  boundaryPanelOpen    : false,
  selectedBoundaryTool : null,
  history              : {
    loading         : false,
    assets          : [],
    assetsLoading   : false,
    hoveredEvent    : null,
    history         : null,
    historyByAsset  : null,
    error           : null,
    startDate       : null,
    endDate         : null,
    dateValue       : 'today',
    geometryRings   : [],
    filterLoading   : false,
    expandedGroups  : [],
    expandedAssets  : [],
    isMobileMapView : false
  },
  settings : {
    showSummaryCards       : true,
    showTripVisible        : true,
    showDistanceTravelled  : true,
    showIdlingTime         : true,
    showDrivingTime        : true,
    showHarshEvents        : true,
    showEventsBetweenTrips : true,
    showTotalAssets        : true
  },
  filters : {
    operators : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    events : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    locations : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    date : {
      startDate : null,
      endDate   : null
    },
    assets : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    groups : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    types : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    },
    categories : {
      loading  : false,
      error    : null,
      items    : [],
      selected : []
    }
  },
  timeline : {
    start          : null,
    end            : null,
    isTimeSliderOn : false
  },
  errorMsg : {
    show           : false,
    currentRequest : null
  },
  shipmentHistory : {
    filters : {
      events : {
        loading  : false,
        error    : null,
        items    : [],
        selected : []
      },
      date : {
        startDate : null,
        endDate   : null,
        dateValue : 'today'
      },
      assets : {
        loading  : false,
        error    : null,
        items    : [],
        selected : []
      },
      groups : {
        loading  : false,
        error    : null,
        items    : [],
        selected : []
      },
      devices : {
        loading  : false,
        error    : null,
        items    : [],
        selected : []
      }
    }
  },
  dvrAssets : {
    data : []
  }
};

// defines the reducers ...
const reducers = {
  [INITIALIZE_REPORTS] : draft => {
    draft.isInitialized            = true;
    const preference               = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState         = preference;
    draft.grid.preferedColumnState = preference;

    const shippingPreference               = getGridColumnState(SHIPPING_PREFERENCE, true);
    draft.shippingGrid.columnState         = shippingPreference;
    draft.shippingGrid.preferedColumnState = shippingPreference;

    const assetHistorySettings = getAssetHistorySetting() || {};
    Object.keys(assetHistorySettings).forEach(key => {
      draft.settings[key] = assetHistorySettings[key];
    });
  },
  [SET_GRID_COLUMNS] : ({grid, history}: ReportsState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : history.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [SET_SHIPPING_GRID_COLUMNS] : ({shippingGrid, history}: ReportsState, {columnState}) => {
    onSetGridColumns({
      grid       : shippingGrid,
      isLoading  : history.loading,
      preference : SHIPPING_PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: ReportsState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [UPDATE_SHIPPING_GRID_COLUMNS] : ({shippingGrid}: ReportsState, {columnState}) => {
    onUpdateGridColumns({grid: shippingGrid, preference: SHIPPING_PREFERENCE}, columnState);
  },
  [SET_HISTORY_ASSETS] : ({history, filters}: ReportsState, {assets}) => {
    history.assets = assets;
    filters.assets.items = assets;
    history.assetsLoading = false;
  },
  [SET_HISTORY_GROUPS] : ({filters}: ReportsState, {groups}) => {
    filters.groups.items = groups;
  },
  [SET_HOVERED_HISTORY_EVENT] : ({history, filters}: ReportsState, {event}) => {
    const isSingleAsset = filters.assets?.selected?.length === 1;
    const asset         =  filters.assets?.selected?.[0];
    if (isSingleAsset && asset?.isMapClick) {
      history.hoveredEvent = {
        ...event,
        isMapClick : asset.isMapClick
      };
    } else {
      history.hoveredEvent = event;
    }
  },
  [GET_ASSETS_HISTORY_REQUEST] : ({history}: ReportsState) => {
    history.error         = null;
    history.loading       = true;
    history.filterLoading = true;
  },
  [REMOVE_ASSETS_HISTORY_REQUEST] : ({history}: ReportsState) => {
    history.error         = null;
    history.loading       = false;
    history.filterLoading = false;
  },
  [GET_ASSETS_HISTORY_RESPONSE] : ({history}: ReportsState, {allHistory, historyByAsset}) => {
    history.history        = allHistory;
    history.historyByAsset = historyByAsset;
    history.loading        = false;
    history.filterLoading  = false;
  },
  [GET_ASSETS_HISTORY_ERROR] : (draft, {error}) => {
    draft.history.error       = error;
    draft.history.loading     = false;
    draft.history.filterLoading = false;
  },
  [SET_ASSETS_HISTORY_LOADING] : ({history}: ReportsState, {loading}) => {
    history.loading = loading;
    history.filterLoading  = false;
  },
  [DATE_FILTER_REQUEST] : ({
    history,
    filters,
    shipmentHistory: {
      filters: shipmentFilters
    }
  }: ReportsState, {date, reportType}) => {
    if (reportType === 'ShipmentHistory') {
      shipmentFilters.date.startDate = date.startDate;
      shipmentFilters.date.endDate   = date.endDate;
      shipmentFilters.date.dateValue = date.dateValue;
    } else {
      history.filterLoading  = true;
      history.startDate      = date.startDate;
      history.endDate        = date.endDate;
      history.dateValue      = date.dateValue;
      filters.date.startDate = date.startDate;
      filters.date.endDate   = date.endDate;
    }
  },
  [UPDATE_DATE_VALUE] : ({history, filters}: ReportsState, {startDate, endDate, dateValue}) => {
    filters.date.dateValue = dateValue;
    filters.date.startDate = startDate;
    filters.date.endDate   = endDate;
  },
  [GET_FILTER_REQUEST] : ({history}: ReportsState) => {
    history.filterLoading = true;
  },
  [APPLY_EVENTS_FILTER] : ({
    history,
    shipmentHistory: {
      filters: shipmentFilters
    },
    filters: {
      events
    }
  }: ReportsState, {selected, reportType}) => {
    if (reportType === 'ShipmentHistory') {
      shipmentFilters.events.selected = selected;
    } else {
      events.selected       = selected;
      history.filterLoading = false;
    }
  },
  [APPLY_CATEGORIES_FILTER] : ({history, filters: {categories}}: ReportsState, {selected}) => {
    categories.selected   = selected;
    history.filterLoading = false;
  },
  [APPLY_TYPES_FILTER] : ({history, filters}: ReportsState, {selected}) => {
    filters.types.selected  = selected;
    history.filterLoading   = false;
  },
  [APPLY_ASSETS_FILTER] : ({
    history,
    shipmentHistory: {
      filters: shipmentFilters
    },
    filters
  }: ReportsState, {selected, reportType}) => {
    if (reportType === 'ShipmentHistory') {
      shipmentFilters.assets.selected = selected;
    } else {
      filters.assets.selected  = selected;
      history.filterLoading    = false;
    }
  },
  [APPLY_GROUPS_FILTER] : ({
    history,
    shipmentHistory: {
      filters: shipmentFilters
    },
    filters
  }: ReportsState, {selected, reportType}) => {
    if (reportType === 'ShipmentHistory') {
      shipmentFilters.groups.selected = selected;
    } else {
      filters.groups.selected = selected;
      history.filterLoading   = false;
    }
  },
  [APPLY_DEVICES_FILTER] : ({
    history,
    shipmentHistory: {
      filters: shipmentFilters
    }
  }: ReportsState, {selected, reportType}) => {
    if (reportType === 'ShipmentHistory') {
      shipmentFilters.devices.selected = selected;
    } else {
      history.filterLoading   = false;
    }
  },
  [ASSET_FILTER_LOADING] : ({history}: ReportsState) => {
    history.assetsLoading = true;
  },
  [GET_OPERATORS_FILTERS_REQUEST] : ({filters:{operators}}: ReportsState) => {
    operators.loading  = true;
    operators.error    = null;
    operators.items    = [];
    operators.selected = [];
  },
  [GET_OPERATORS_FILTERS_RESPONSE] : ({filters:{operators}}: ReportsState, {items}) => {
    operators.loading  = false;
    operators.items    = items;
    operators.selected = items;
  },
  [GET_OPERATORS_FILTERS_ERROR] : ({filters:{operators}}: ReportsState, {error}) => {
    operators.loading = false;
    operators.error   = error;
  },
  [SET_OPERATORS_FILTERS_REQUEST] : ({filters:{operators}}: ReportsState, {value}) => {
    operators.selected = value;
  },
  [GET_EVENTS_FILTERS_REQUEST] : ({filters:{events}}: ReportsState) => {
    events.loading  = true;
    events.error    = null;
    events.items    = [];
    events.selected = [];
  },
  [GET_EVENTS_FILTERS_RESPONSE] : ({filters:{events}}: ReportsState, {items}) => {
    events.items    = items;
    events.selected = events.selected?.length ? events.selected : items;
    events.loading  = false;
  },
  [GET_EVENTS_FILTERS_ERROR] : ({filters:{events}}: ReportsState, {error}) => {
    events.loading = false;
    events.error   = error;
  },
  [SET_EVENTS_FILTERS_REQUEST] : ({filters:{events}}: ReportsState, {value}) => {
    events.selected = value;
  },
  [GET_LOCATIONS_FILTERS_REQUEST] : ({filters:{locations}}: ReportsState) => {
    locations.loading  = true;
    locations.error    = null;
    locations.items    = [];
    locations.selected = [];
  },
  [GET_LOCATIONS_FILTERS_RESPONSE] : ({filters:{locations}}: ReportsState, {items}) => {
    locations.loading  = false;
    locations.items    = items;
    locations.selected = items;
  },
  [GET_LOCATIONS_FILTERS_ERROR] : ({filters:{locations}}: ReportsState, {error}) => {
    locations.loading = false;
    locations.error   = error;
  },
  [SET_LOCATIONS_FILTERS_REQUEST] : ({filters:{locations}}: ReportsState, {value}) => {
    locations.selected = value;
  },
  [GET_DATE_FILTERS_REQUEST] : ({filters:{date}}: ReportsState) => {
    date.loading  = true;
    date.error    = null;
    date.items    = [];
    date.selected = [];
  },
  [GET_DATE_FILTERS_RESPONSE] : ({filters:{date}}: ReportsState, {items}) => {
    date.loading  = false;
    date.items    = items;
    date.selected = [items[0]];
  },
  [GET_DATE_FILTERS_ERROR] : ({filters:{date}}: ReportsState, {error}) => {
    date.loading = false;
    date.error   = error;
  },
  [SET_DATE_FILTERS_REQUEST] : ({filters:{date}}: ReportsState, {value}) => {
    date.selected = value;
  },
  [REGISTER_TOKEN_SUCCESS] : (draft: ReportsState) => {
    draft.isTokenRegistered = true;
  },
  [SET_SISENSE_DEFAULT_DASHBOARD] : ({sisense}: ReportsState, {id}) => {
    sisense.defaultDashboard = id;
  },
  [TOGGLE_GRID_GROUPS_EXPANDED] : ({history: {expandedGroups}}: ReportsState, {id, isExpanded}) => {
    if (!isExpanded) {
      const index = expandedGroups.indexOf(id);
      expandedGroups.splice(index, 1);
    } else {
      expandedGroups.push(id);
    }
  },
  [TOGGLE_GRID_ASSETS_EXPANDED] : ({history: {expandedAssets}}: ReportsState, {id, isExpanded}) => {
    if (!isExpanded) {
      const index = expandedAssets.indexOf(id);
      expandedAssets.splice(index, 1);
    } else {
      if (!(expandedAssets.includes(id))) {
        expandedAssets.push(id);
      }
    }
  },
  [SET_SELECTED_ASSET] : ({history}: ReportsState) => {
    history.expandedGroups = [];
  },
  [SET_HISTORY_DATE_RANGE] : ({history, filters}: ReportsState, {start, end}) => {
    history.startDate = start;
    filters.date.startDate = start;
    history.endDate = end;
    filters.date.endDate = end;
  },
  [CLEAR_FILTERS] : ({filters, history, shipmentHistory: {filters : shipmentFilters}}: ReportsState, {reportType}) => {
    if (reportType !== ReportsMode.ShipmentHistory) {
      filters.assets.selected = [];
      filters.types.selected = [];
      filters.categories.selected = [];
      filters.groups.selected = [];
      filters.events.selected = [];
      filters.date.startDate = null;
      filters.date.endDate  = null;
      shipmentFilters.date.dateValue = DateSelectorValues.Live;
    } else {
      shipmentFilters.assets.selected = [];
      shipmentFilters.devices.selected = [];
      shipmentFilters.groups.selected = [];
      shipmentFilters.events.selected = [];
      shipmentFilters.date.startDate = null;
      shipmentFilters.date.endDate  = null;
      shipmentFilters.date.dateValue = DateSelectorValues.Today;
    }
  },
  [SET_TIMELINE_DATE_RANGE] : ({timeline}: ReportsState, {start, end}) => {
    timeline.start = start;
    timeline.end   = end;
  },
  [TOGGLE_TIMESLIDER] : ({timeline}: ReportsState) => {
    timeline.isTimeSliderOn = !timeline.isTimeSliderOn;
  },
  [TOGGLE_SHOW_EVENTS_BETWEEN_TRIPS] : ({settings}: ReportsState) => {
    settings.showEventsBetweenTrips = !settings.showEventsBetweenTrips;
  },
  [TOGGLE_DISTANCE_TRAVELLED_CARD] : ({settings}: ReportsState) => {
    settings.showDistanceTravelled = !settings.showDistanceTravelled;
  },
  [TOGGLE_IDLING_TIME_CARD] : ({settings}: ReportsState) => {
    settings.showIdlingTime = !settings.showIdlingTime;
  },
  [TOGGLE_DRIVING_TIME_CARD] : ({settings}: ReportsState) => {
    settings.showDrivingTime = !settings.showDrivingTime;
  },
  [TOGGLE_HARSH_EVENTS_CARD] : ({settings}: ReportsState) => {
    settings.showHarshEvents = !settings.showHarshEvents;
  },
  [TOGGLE_TOTAL_ASSETS_CARD] : ({settings}: ReportsState) => {
    settings.showTotalAssets = !settings.showTotalAssets;
  },
  [TOGGLE_SHOW_TRIP_VISIBLE] : ({settings, grid}: ReportsState, {state}) => {
    settings.showTripVisible = state;
    if (grid.columnState) {
      grid.columnState = updateTripVisible(grid.columnState, state);
    };
  },
  [TOGGLE_SHOW_SUMMARY_CARDS] : ({settings}: ReportsState) => {
    const val = !settings.showSummaryCards;
    settings.showSummaryCards = val;
    settings.showTotalAssets  = val;
    settings.showDrivingTime  = settings.showHarshEvents       = val;
    settings.showIdlingTime   = settings.showDistanceTravelled = val;
  },
  [TOGGLE_SHOW_SUMMARY_CARD] : ({settings}: ReportsState, {show}) => {
    settings.showSummaryCards = show;
  },
  [SET_BOUNDARY_PANEL_OPEN] : (draft, {boundaryPanelOpen}) => {
    draft.boundaryPanelOpen = boundaryPanelOpen;
  },
  [SET_SELECTED_BOUNDARY_TOOL] : (draft: ReportsState, {selectedBoundaryTool}) => {
    draft.selectedBoundaryTool = selectedBoundaryTool;
  },
  [SET_ASSETS_HISTORY_REPORT_ERROR] : ({history, errorMsg}: ReportsState, {show, currentRequest}) => {
    errorMsg.show = show;
    errorMsg.currentRequest = currentRequest;
    history.filterLoading = false;
  },
  [SET_IS_MOBILE_MAP_VIEW] : ({history}: ReportsState, {isMobileMapView}) => {
    history.isMobileMapView = isMobileMapView;
  },
  [SET_DVR_ASSETS] : (draft, {data}) => {
    draft.dvrAssets.data = data;
    draft.dvrAssets.loading = false;
  },
  [SET_HISTORY_MAP_PROPS] : (draft: ReportsState, {props}) => {
    draft.mapProps = props;
  },
  [SET_HISTORY_GRID_FILTER_MODEL] : ({gridFilters}: ReportsState, {filterModel}) => {
    gridFilters.filterModel = filterModel;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: PageState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
